import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';

import BasicButton from './BasicButton';
import LayoutProvider from '../../utils/styles/layout/LayoutProvider';
import withTheme from '../../utils/styles/theme/withTheme';
import { GeneralType } from '../../../../types';
import { GENERAL_TYPES } from '../../utils/styles/constants';
import Amplitude from '../../libs/amplitude/Amplitude';
import { K_AMPLITUDE_EVENT_BUTTON_CLICK } from '../../libs/amplitude/events';
import { css } from 'styled-components';
import LoadingDots from './LoadingDots';
import { useAsync } from 'react-async';

type Props = {
  type: GeneralType;
  active?: boolean;
  inverse?: boolean;
};

const Button = withTheme(
  ({
    theme,
    type: _type = 'primary',
    active = true,
    disabled,
    inverse = false,
    text,
    children,
    onClick,
    loading: forceLoading = false,
    soft = false,
    buttonType = undefined,
    ...rest
  }) => {
    const { isLoading: _loading, error, data, run: handleClick } = useAsync({ deferFn: onClick });
    const loading = forceLoading || _loading;
    let type = disabled ? 'gray' : _type;
    if (!GENERAL_TYPES.find(cType => cType === type)) {
      type = 'primary';
    }
    let backgroundColor = active
      ? soft
        ? theme.colors[type].light
        : theme.colors[type].primary
      : undefined;
    let borderColor = active ? undefined : theme.colors[type].primary;
    let color = active
      ? soft
        ? theme.colors[type].primary
        : theme.colors.light.primary
      : theme.colors[type].primary;
    if (type === 'light') {
      borderColor = theme.colors.light.secondary;
      color = theme.colors.light.secondary;
    }
    if (inverse) {
      [color, backgroundColor] = [backgroundColor, color];
    }

    return (
      <Amplitude>
        {({ instrument }) => (
          <LayoutProvider
            value={{
              backgroundColor,
              borderColor,
              text: {
                color,
                fontWeight: 600
              }
            }}
          >
            <BasicButton
              type={buttonType}
              css={`
                color: ${color};
                font-weight: 600;
                ${!disabled &&
                  css`
                    transition: all ease-in-out 0.2s;
                    box-shadow: 0px 1px 5px rgba(0, 16, 17, 0.1), 0px 3px 4px rgba(0, 16, 17, 0.06),
                      0px 2px 4px rgba(0, 16, 17, 0.07);

                    :hover {
                      box-shadow: 0px 1px 5px rgba(0, 32, 34, 0.2),
                        0px 3px 4px rgba(0, 32, 34, 0.12), 0px 2px 4px rgba(0, 32, 34, 0.14);
                    }
                  `}
              `}
              disabled={disabled || loading}
              onClick={instrument(K_AMPLITUDE_EVENT_BUTTON_CLICK, handleClick, { text })}
              {...rest}
            >
              {children}
              {!isEmpty(text) && text}
              {loading && (
                <div
                  css={css`
                    position: absolute;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    top: 0;
                    background: rgba(255, 255, 255, 0.8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  `}
                >
                  <LoadingDots color={backgroundColor || borderColor} />
                </div>
              )}
            </BasicButton>
          </LayoutProvider>
        )}
      </Amplitude>
    );
  }
);

export default Button;
